import { Button, Card, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/nl';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CardBgcolor } from '../../app/AppStyles';
import { ConvertoToCSharpDateTime } from '../../app/AppUtils';
import { setAlertMessage, setAlertSeverity, setAlertStatus } from '../subcomponents/alertSlice';
import BackButton from '../subcomponents/BackButton';
import ContentLoader from '../subcomponents/ContentLoader';
import ContentSelector from '../subcomponents/ContentSelector';
import PageLayout from '../subcomponents/PageLayout';
import { fetchGroupAttendanceSchoolclasses, postGroupAttendance, resetGroupAttendanceStatus } from './attendanceSlice';

const cardDisplay = {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    p: 2,
    textAlign: 'center',
    bgcolor: CardBgcolor
}

export default function GroupAttendancePage() {
    const dispatch = useDispatch();

    const { groupAttendanceSchoolclasses, groupAttendancePost } = useSelector((state) => state.attendance);
    const { user } = useSelector((state) => state.user);
    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { aspNetUser } = useSelector((state) => state.settings.app);

    const [newGroupAttendance, setNewGroupAttendance] = useState();
    const [selectedSchoolclasses, setSelectedSchoolclasses] = useState([]);
    const [selectedStartDate, setSelectedStartDate] = useState();
    const [selectedEndDate, setSelectedEndDate] = useState();

    useEffect(() => {
        dispatch(fetchGroupAttendanceSchoolclasses());
    }, []);

    const handleSaveGroupAttendaceClick = () => {
        newGroupAttendance.schoolclassIds = selectedSchoolclasses.map((item) => item.schoolclassId);
        newGroupAttendance.attendanceStart = ConvertoToCSharpDateTime(selectedStartDate);
        newGroupAttendance.attendanceEnd = ConvertoToCSharpDateTime(selectedEndDate);
        newGroupAttendance.createdBy = `AP/${user.number}`;

        dispatch(postGroupAttendance(newGroupAttendance))
    }

    const handleValueReset = () => {
        setNewGroupAttendance(null);
        setSelectedSchoolclasses([]);
        setSelectedStartDate(null);
        setSelectedEndDate(null);
    }

    useEffect(() => {
        if (groupAttendancePost.status === "success") {
            dispatch(setAlertStatus(true));
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.SAVE_SUCCESS}`));
            dispatch(setAlertSeverity("success"));
            dispatch(resetGroupAttendanceStatus());
            handleValueReset();
        } else if (groupAttendancePost.status === "failed") {
            dispatch(setAlertStatus(true));
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.SAVE_ERROR}`));
            dispatch(setAlertSeverity("error"));
            dispatch(resetGroupAttendanceStatus());
        };
    }, [dispatch, groupAttendancePost.status]);

    return (
        <PageLayout
            title={`${resourceFinal[appLanguage].words.ATTENDANCE_GROUP}`}
            content={
                <ContentLoader
                    successCondition={groupAttendanceSchoolclasses.status === "success"}
                    errorCondition={groupAttendanceSchoolclasses.status === "failed"}
                    content={
                        <Stack spacing={1}>
                            <Card elevation={0} sx={{ ...cardDisplay }}>
                                <Typography sx={{ color: 'error.main', fontWeight: 'bold' }}>{resourceFinal[appLanguage].messages.ATTENDANCE_GROUP_WARNING}</Typography>
                            </Card>
                            <ContentSelector
                                content={groupAttendanceSchoolclasses.data}
                                contentId="schoolclassId"
                                contentTitle={resourceFinal[appLanguage].words.CLASSES}
                                contentHeaders={[resourceFinal[appLanguage].words.NAME]}
                                contentRows={["schoolclassName"]}
                                selectedContent={selectedSchoolclasses}
                                setSelectedContent={setSelectedSchoolclasses}
                                multiple
                            />
                            <Card elevation={0} sx={{ ...cardDisplay }}>
                                <Typography>
                                    {resourceFinal[appLanguage].words.CLASSES}: {selectedSchoolclasses?.map((item, index) => (index ? ', ' : '') + (item.schoolclassName ? item.schoolclassName : "-"))}
                                </Typography>
                            </Card>
                            <Card elevation={0} sx={{ ...cardDisplay }}>
                                <Grid container spacing={1}>
                                    <Grid size={6}>
                                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nl">
                                            <DateTimePicker
                                                value={selectedStartDate}
                                                slotProps={{ textField: { fullWidth: true } }}
                                                label={resourceFinal[appLanguage].words.START}
                                                onChange={(value) => {
                                                    setSelectedStartDate(dayjs(value));
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </Grid>
                                    <Grid size={6}>
                                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nl">
                                            <DateTimePicker
                                                value={selectedEndDate}
                                                slotProps={{ textField: { fullWidth: true } }}
                                                label={resourceFinal[appLanguage].words.END}
                                                onChange={(value) => {
                                                    setSelectedEndDate(dayjs(value));
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </Grid>
                                </Grid>
                            </Card>
                            <Card elevation={0} sx={{ ...cardDisplay }}>
                                <FormControl fullWidth sx={{ textAlign: 'left' }}>
                                    <InputLabel>{resourceFinal[appLanguage].words.STATUS}</InputLabel>
                                    <Select
                                        value={newGroupAttendance?.attendanceStatus || ''}
                                        onChange={(e) => {
                                            setNewGroupAttendance({
                                                ...newGroupAttendance,
                                                attendanceStatus: e.target.value
                                            });
                                        }}
                                        variant="standard"
                                    >
                                        <MenuItem value={"present"}>{resourceFinal[appLanguage].words.ATTENDANCE_PRESENT}</MenuItem>
                                        <MenuItem value={"absent"}>{resourceFinal[appLanguage].words.ATTENDANCE_ABSENT}</MenuItem>
                                        <MenuItem value={"compensated"}>{resourceFinal[appLanguage].words.ATTENDANCE_COMPENSATED}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Card>
                            <Card elevation={0} sx={{ ...cardDisplay }}>
                                <TextField
                                    fullWidth multiline
                                    value={newGroupAttendance?.attendanceRemark || ''}
                                    label={resourceFinal[appLanguage].words.PERFORMANCE_REMARK}
                                    onChange={(event) => setNewGroupAttendance({
                                        ...newGroupAttendance,
                                        attendanceRemark: event.target.value
                                    })}
                                    variant="outlined"
                                />
                            </Card>
                            <Card elevation={0} sx={{ ...cardDisplay }}>
                                <Stack direction="row" justifyContent="center" spacing={1} sx={{ w: 1 }}>
                                    {groupAttendancePost.status === "loading" && <CircularProgress color="primary" />}
                                    <Button
                                        disabled={
                                            selectedSchoolclasses?.length <= 0 ||
                                            !selectedStartDate ||
                                            !selectedEndDate ||
                                            !newGroupAttendance?.attendanceStatus ||
                                            groupAttendancePost.status === "loading" ||
                                            !aspNetUser?.role?.includes("Attendances")
                                        }
                                        variant="outlined"
                                        color="primary"
                                        onClick={handleSaveGroupAttendaceClick}
                                        sx={{ width: 'auto' }}
                                    >
                                        {resourceFinal[appLanguage].words.SAVE}
                                    </Button>
                                </Stack>
                            </Card>
                        </Stack>
                    }
                />
            }
            contentRight={
                <BackButton desktop />
            }
            bottomBar
            disableContentBackground
        />
    );
}
